import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus, faTrash, faUsers } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

import {
  useProviderUsers,
  useAddProviderUser,
  useRemoveProviderUser,
} from '@/api/useProviderUsers';
import { Button, Card, Spinner } from '@/components/ui';
import { Input, Select } from '@/components/forms';
import styles from './ProviderUsersSection.module.css';

interface ProviderUsersSectionProps {
  providerId: string;
}

const ROLE_OPTIONS = [
  { value: 'owner', label: 'Owner' },
  { value: 'editor', label: 'Editor' },
];

function getInitials(firstName: string, lastName: string, email: string): string {
  if (firstName || lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  return email.charAt(0).toUpperCase();
}

function getDisplayName(firstName: string, lastName: string, email: string): string {
  const name = `${firstName} ${lastName}`.trim();
  return name || email;
}

export function ProviderUsersSection({ providerId }: ProviderUsersSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [email, setEmail] = useState('');
  const [providerRole, setProviderRole] = useState('editor');

  const { data: users, isLoading } = useProviderUsers(providerId);
  const addUser = useAddProviderUser(providerId);
  const removeUser = useRemoveProviderUser(providerId);

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Email is required.');
      return;
    }
    try {
      await addUser.mutateAsync({ email: email.trim(), provider_role: providerRole });
      toast.success('User added to provider.');
      setEmail('');
      setProviderRole('editor');
      setShowAddForm(false);
    } catch (err: unknown) {
      const errorData = (
        err as { response?: { data?: { detail?: string; code?: string; email?: string[] } } }
      )?.response?.data;
      if (errorData?.code === 'user_not_found') {
        const confirmed = window.confirm(`User "${email.trim()}" not found. Send an invitation?`);
        if (confirmed) {
          try {
            await addUser.mutateAsync({
              email: email.trim(),
              provider_role: providerRole,
              invite: true,
            });
            toast.success(`Invitation sent to ${email.trim()}`);
            setEmail('');
            setProviderRole('editor');
            setShowAddForm(false);
          } catch {
            toast.error('Failed to send invitation.');
          }
        }
      } else {
        const message = errorData?.detail || errorData?.email?.[0] || 'Failed to add user.';
        toast.error(message);
      }
    }
  };

  const handleRemove = async (providerUserId: string, userEmail: string) => {
    const confirmed = window.confirm(
      `Remove ${userEmail} from this provider? They will lose access.`,
    );
    if (!confirmed) return;
    try {
      await removeUser.mutateAsync(providerUserId);
      toast.success('User removed from provider.');
    } catch {
      toast.error('Failed to remove user.');
    }
  };

  return (
    <Card className={styles.section}>
      <div className={styles.sectionInner}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleGroup}>
            <FontAwesomeIcon icon={faUsers} className={styles.headerIcon} />
            <h3 className={styles.title}>Team Members</h3>
            {users && users.length > 0 && <span className={styles.count}>{users.length}</span>}
          </div>
          <Button variant="primary" size="sm" onClick={() => setShowAddForm((v) => !v)}>
            <FontAwesomeIcon icon={faUserPlus} />
            {showAddForm ? 'Cancel' : 'Add User'}
          </Button>
        </div>

        {/* Add User Form */}
        {showAddForm && (
          <form onSubmit={handleAddSubmit} className={styles.addForm}>
            <div className={styles.addFormFields}>
              <div className={styles.emailField}>
                <Input
                  name="user-email"
                  label="User Email"
                  value={email}
                  setValue={setEmail}
                  variant="bordered"
                  placeholder="user@example.com"
                />
              </div>
              <div className={styles.roleField}>
                <Select
                  value={providerRole}
                  onChange={(e) => setProviderRole(e.target.value)}
                  options={ROLE_OPTIONS}
                  label="Role"
                  size="md"
                />
              </div>
            </div>
            <div className={styles.addFormActions}>
              <Button type="submit" variant="primary" size="sm" disabled={addUser.isPending}>
                {addUser.isPending ? 'Adding...' : 'Add User'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setEmail('');
                  setProviderRole('editor');
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* User List */}
        {isLoading ? (
          <div className={styles.loadingState}>
            <Spinner />
          </div>
        ) : !users || users.length === 0 ? (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faUsers} className={styles.emptyIcon} />
            <p>No team members linked to this provider.</p>
            <p className={styles.emptyHint}>Add a user above to give them access.</p>
          </div>
        ) : (
          <div className={styles.userList}>
            {users.map((member) => (
              <div key={member.id} className={styles.userRow}>
                {/* Avatar */}
                <div className={styles.avatar}>
                  {getInitials(member.user_first_name, member.user_last_name, member.user_email)}
                </div>

                {/* Info */}
                <div className={styles.userInfo}>
                  <span className={styles.userName}>
                    {getDisplayName(
                      member.user_first_name,
                      member.user_last_name,
                      member.user_email,
                    )}
                  </span>
                  <span className={styles.userEmail}>{member.user_email}</span>
                  {member.invited_by_email && (
                    <span className={styles.invitedBy}>Invited by {member.invited_by_email}</span>
                  )}
                </div>

                {/* Role badge */}
                <span
                  className={`${styles.roleBadge} ${
                    member.provider_role === 'owner' ? styles.roleOwner : styles.roleEditor
                  }`}
                >
                  {member.provider_role}
                </span>

                {/* Remove */}
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemove(member.id, member.user_email)}
                  disabled={removeUser.isPending}
                  title="Remove user"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
